import React from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { useTenant } from '@/contexts/TenantContext'
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  FileText, 
  Shield, 
  Zap,
  Activity,
  Cpu,
  Database,
  Globe,
  Music,
  Palette,
  Mic,
  Guitar,
  Sparkles
} from 'lucide-react'
import CosmicCard, { MetricCard, HolographicCard, DataCard, StatusCard } from '@/components/ui/CosmicCard'
import CosmicButton from '@/components/ui/CosmicButton'
import { staggerContainer, staggerItem } from '@/lib/animations'

const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const { isClient, isCreator, isInternal } = useTenant()

  const getPortalType = () => {
    if (isClient) return { title: 'Enterprise Portal', subtitle: 'Client Management System', icon: Cpu };
    if (isCreator) return { title: 'Creative Hub', subtitle: 'Artist & Creator Hub', icon: Music };
    if (isInternal) return { title: 'Admin Panel', subtitle: 'System Administration', icon: Shield };
    return { title: 'COSMK Vault', subtitle: 'Digital Rights Management', icon: Globe };
  }

  const portalInfo = getPortalType()
  const PortalIcon = portalInfo.icon

  const metrics = [
    { title: 'Total Assets', value: '2,847', change: '+12%', trend: 'up' as const, icon: FileText },
    { title: 'Active Users', value: '1,234', change: '+5%', trend: 'up' as const, icon: Users },
    { title: 'Revenue', value: '$45.2K', change: '+18%', trend: 'up' as const, icon: TrendingUp },
    { title: 'Security Score', value: '98.5%', change: '+2%', trend: 'up' as const, icon: Shield },
  ]

  const recentActivity = [
    { action: 'Content Upload', user: 'john.doe@company.com', time: '2 minutes ago', status: 'success' },
    { action: 'License Generated', user: 'jane.smith@studio.com', time: '5 minutes ago', status: 'success' },
    { action: 'Security Alert', user: 'system', time: '10 minutes ago', status: 'warning' },
    { action: 'API Call', user: 'api-client-001', time: '15 minutes ago', status: 'info' },
  ]

  return (
    <motion.div
      className="space-y-6"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Welcome Section */}
      <motion.div variants={staggerItem}>
        <HolographicCard
          title="Welcome to COSMK Vault"
          subtitle={`${portalInfo.title} - ${portalInfo.subtitle}`}
          icon={<PortalIcon />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-display font-semibold text-white mb-2">
                Hub Status: Live
              </h3>
              <p className="text-stellar-cyan font-mono text-sm">
                All systems operational. Ready for creative expression.
              </p>
            </div>
            <div className="flex justify-end">
              <motion.div
                className="w-16 h-16 bg-stellar-gradient rounded-full flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-8 h-8 text-white" />
              </motion.div>
            </div>
          </div>
        </HolographicCard>
      </motion.div>

      {/* Metrics Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        variants={staggerContainer}
      >
        {metrics.map((metric, index) => (
          <motion.div key={metric.title} variants={staggerItem} custom={index}>
            <MetricCard
              title={metric.title}
              value={metric.value}
              change={metric.change}
              trend={metric.trend}
              icon={<metric.icon />}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Analytics Chart */}
        <motion.div className="lg:col-span-2" variants={staggerItem}>
          <DataCard
            title="Performance Analytics"
            subtitle="Real-time creative metrics"
            icon={<BarChart3 />}
          >
            <div className="h-64 flex items-center justify-center">
              <motion.div
                className="w-full h-32 bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/30 relative overflow-hidden"
                whileHover={{ scale: 1.02 }}
              >
                {/* Animated chart bars */}
                {[20, 40, 60, 80, 60, 40, 80].map((height, index) => (
                  <motion.div
                    key={index}
                    className="absolute bottom-0 bg-stellar-gradient rounded-t"
                    style={{
                      left: `${(index * 14) + 2}%`,
                      width: '10%',
                      height: `${height}%`
                    }}
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                  />
                ))}
                
                {/* Scan line effect */}
                <motion.div
                  className="absolute top-0 left-0 w-full h-0.5 bg-stellar-blue"
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </motion.div>
            </div>
          </DataCard>
        </motion.div>

        {/* Recent Activity */}
        <motion.div variants={staggerItem}>
          <StatusCard
            title="Recent Activity"
            subtitle="Live studio events"
            icon={<Activity />}
          >
            <div className="space-y-3">
              {recentActivity.map((activity, index) => (
                <motion.div
                  key={index}
                  className="flex items-center justify-between p-3 bg-cosmic-dark/30 rounded-lg border border-stellar-blue/20"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-mono text-white">{activity.action}</p>
                    <p className="text-xs font-mono text-stellar-cyan">{activity.user}</p>
                    <p className="text-xs font-mono text-stellar-cyan/70">{activity.time}</p>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${
                    activity.status === 'success' ? 'bg-stellar-green' :
                    activity.status === 'warning' ? 'bg-warning-amber' :
                    'bg-stellar-blue'
                  }`} />
                </motion.div>
              ))}
            </div>
          </StatusCard>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div variants={staggerItem}>
        <DataCard
          title="Quick Actions"
          subtitle="Common creative operations"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CosmicButton
              variant="primary"
              icon={<FileText />}
              className="w-full"
            >
              Upload Content
            </CosmicButton>
            
            <CosmicButton
              variant="secondary"
              icon={<Shield />}
              className="w-full"
            >
              Security Scan
            </CosmicButton>
            
            <CosmicButton
              variant="success"
              icon={<Database />}
              className="w-full"
            >
              Generate Report
            </CosmicButton>
          </div>
        </DataCard>
      </motion.div>

      {/* System Status */}
      <motion.div variants={staggerItem}>
        <HolographicCard
          title="Hub Status"
          subtitle="Real-time monitoring"
          icon={<Cpu />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'Database', status: 'online', icon: Database },
              { name: 'API Gateway', status: 'online', icon: Globe },
              { name: 'Security', status: 'online', icon: Shield },
              { name: 'Analytics', status: 'online', icon: BarChart3 },
            ].map((system, index) => (
              <motion.div
                key={system.name}
                className="flex items-center justify-between p-3 bg-cosmic-dark/50 rounded-lg border border-stellar-blue/20"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3">
                  <system.icon className="w-5 h-5 text-stellar-blue" />
                  <div>
                    <p className="text-sm font-mono text-white">{system.name}</p>
                    <p className="text-xs font-mono text-stellar-green uppercase">
                      {system.status}
                    </p>
                  </div>
                </div>
                <motion.div
                  className="w-3 h-3 bg-stellar-green rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </motion.div>
            ))}
          </div>
        </HolographicCard>
      </motion.div>
    </motion.div>
  )
}

export default Dashboard 